import React from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";

// Eagerly load critical pages and main section pages
import Index from "./pages/Index";
import Auth from "./pages/Auth";
import Admin from "./pages/Admin";
import SuperAdmin from "./pages/SuperAdmin";

// Eagerly load all pages to eliminate loading screens
import SuperAdminOrganization from "./pages/SuperAdminOrganization";
import SuperAdminClub from "./pages/SuperAdminClub";
import SuperAdminTeam from "./pages/SuperAdminTeam";
import SuperAdminPlayer from "./pages/SuperAdminPlayer";
import SuperAdminEmails from "./pages/SuperAdminEmails";
import SuperAdminPaymentConfig from "./pages/SuperAdminPaymentConfig";
import PlayerProfile from "./pages/PlayerProfile";
import CoachProfile from "./pages/CoachProfile";
import NotFound from "./pages/NotFound";
import AccessDenied from "./pages/AccessDenied";
import InviteAcceptance from "./pages/InviteAcceptance";
import EmailConfirmation from "./pages/EmailConfirmation";
import Logout from "./pages/Logout";
import TeamPlayerActivity from "./pages/TeamPlayerActivity";
import PlayerProgram from "./pages/PlayerProgram";
import QuizResultsPage from "./pages/QuizResultsPage";
import TestDeepLink from "./pages/TestDeepLink";
// Create QueryClient instance inside component to avoid SSR issues
const App: React.FC = () => {
  const queryClient = React.useMemo(() => new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 1000 * 60 * 5, // 5 minutes
        gcTime: 1000 * 60 * 10, // 10 minutes
      },
    },
  }), []);

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/debug-auth" element={<TestDeepLink />} />
              <Route path="/admin" element={<Admin />} />
              
              {/* Protected SuperAdmin Routes */}
              <Route 
                path="/superadmin" 
                element={
                  <ProtectedRoute requiredRole="superadmin" redirectTo="/access-denied">
                    <SuperAdmin />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/superadmin/org/:orgId" 
                element={
                  <ProtectedRoute requiredRole="superadmin" redirectTo="/access-denied">
                    <SuperAdminOrganization />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/superadmin/club/:clubId" 
                element={
                  <ProtectedRoute requiredRole="superadmin" redirectTo="/access-denied">
                    <SuperAdminClub />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/superadmin/team/:teamId" 
                element={
                  <ProtectedRoute requiredRole="superadmin" redirectTo="/access-denied">
                    <SuperAdminTeam />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/superadmin/player/:playerId" 
                element={
                  <ProtectedRoute requiredRole="superadmin" redirectTo="/access-denied">
                    <SuperAdminPlayer />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/superadmin/emails" 
                element={
                  <ProtectedRoute requiredRole="superadmin" redirectTo="/access-denied">
                    <SuperAdminEmails />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/superadmin/payment-config" 
                element={
                  <ProtectedRoute requiredRole="superadmin" redirectTo="/access-denied">
                    <SuperAdminPaymentConfig />
                  </ProtectedRoute>
                } 
              />
              
              {/* Protected Player/Team Routes */}
              <Route 
                path="/player/:playerId/programs" 
                element={
                  <ProtectedRoute requireAuth>
                    <TeamPlayerActivity />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/player/:playerId/programs/:programId" 
                element={
                  <ProtectedRoute requireAuth>
                    <PlayerProgram />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/player/:playerId/programs/:programId/quiz/:quizId/results/:attemptId" 
                element={
                  <ProtectedRoute requireAuth>
                    <QuizResultsPage />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/player/:playerId" 
                element={
                  <ProtectedRoute requireAuth>
                    <PlayerProfile />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/coach/:coachId" 
                element={
                  <ProtectedRoute requireAuth>
                    <CoachProfile />
                  </ProtectedRoute>
                } 
              />
              <Route path="/invite/:token" element={<InviteAcceptance />} />
              <Route path="/auth/confirm" element={<EmailConfirmation />} />
              <Route path="/logout" element={<Logout />} />
              <Route path="/access-denied" element={<AccessDenied />} />
              
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </BrowserRouter>
        </TooltipProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
};

export default App;
